class StockistFinder extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-stockist-grid]');
    this.panel = this.querySelector('[data-stockist-panel]');
    this.filters = [...this.querySelectorAll('[data-stockist-filter]')];
    this.pins = [...this.querySelectorAll('[data-stockist-pin]')];
    this.items = [...this.querySelectorAll('[data-stockist-item]')];
    this.bars = [...this.querySelectorAll('[data-stockist-bar]')];
    this.cards = [...this.querySelectorAll('[data-stockist-card]')];
    this.empty = this.querySelector('[data-stockist-empty]');
    this.line = this.querySelector('[data-stockist-line]');
    this.stateSelect = this.querySelector('[data-stockist-select]');

    this.location = 'all';
    this.selected = null;
    this.previewed = null;

    this.snapPinsToLand();

    this.filters.forEach((filter) => {
      filter.addEventListener('click', () => this.setLocation(filter.dataset.stockistFilter));
    });

    this.stateSelect?.addEventListener('change', () => this.setLocation(this.stateSelect.value));

    this.pins.forEach((pin) => {
      pin.addEventListener('mouseenter', () => this.preview(pin.dataset.stockistPin));
      pin.addEventListener('mouseleave', () => this.preview(null));
      pin.addEventListener('focus', () => this.preview(pin.dataset.stockistPin));
      pin.addEventListener('blur', () => this.preview(null));
      pin.addEventListener('click', () => {
        this.select(pin.dataset.stockistPin);
        this.revealPanel();
      });
    });

    this.bars.forEach((bar) => {
      bar.addEventListener('click', () => this.select(bar.dataset.stockistBar));
    });

    this.querySelectorAll('[data-stockist-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.onResize = debounce(() => this.drawLine(), 100);
    window.addEventListener('resize', this.onResize);

    this.setLocation('all');
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  // The stylised map's coastline doesn't follow the real one, so coastal stockists can project
  // into the sea. Any pin whose tip isn't comfortably on land is moved to the nearest point that is.
  snapPinsToLand() {
    const svg = this.querySelector('[data-stockist-map] svg');
    const shapes = svg ? [...svg.querySelectorAll('path')] : [];
    if (!shapes.length || !shapes[0].isPointInFill) return;

    const { width, height } = svg.viewBox.baseVal;
    const margin = 6; // Distance in map units the pin tip must be from the coast
    const point = svg.createSVGPoint();
    const directions = Array.from({ length: 16 }, (_, i) => (i / 16) * Math.PI * 2);

    const onLand = (x, y) => {
      point.x = x;
      point.y = y;
      return shapes.some((shape) => shape.isPointInFill(point));
    };
    const isSafe = (x, y) => onLand(x, y)
      && directions.every((angle) => onLand(x + Math.cos(angle) * margin, y + Math.sin(angle) * margin));

    this.pins.forEach((pin) => {
      const x = (parseFloat(pin.style.left) / 100) * width;
      const y = (parseFloat(pin.style.top) / 100) * height;
      if (isSafe(x, y)) return;

      for (let radius = 1; radius <= 60; radius++) {
        const angle = directions.find((a) => isSafe(x + Math.cos(a) * radius, y + Math.sin(a) * radius));
        if (angle === undefined) continue;

        pin.style.left = `${((x + Math.cos(angle) * radius) / width) * 100}%`;
        pin.style.top = `${((y + Math.sin(angle) * radius) / height) * 100}%`;
        return;
      }
    });
  }

  // On mobile the panel sits above the map, so bring it into view when a pin is tapped
  revealPanel() {
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    this.panel.scrollTop = 0;
    if (this.panel.getBoundingClientRect().top < 0) {
      this.panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  get current() {
    return this.previewed || this.selected;
  }

  isVisible(element) {
    return this.location === 'all'
      ? element.hasAttribute('data-primary')
      : element.dataset.location === this.location;
  }

  setLocation(location) {
    this.location = location;
    if (this.stateSelect) this.stateSelect.value = location;
    this.panel.scrollTop = 0;

    this.filters.forEach((filter) => {
      const active = filter.dataset.stockistFilter === location;
      filter.dataset.state = active ? 'active' : 'inactive';
      filter.setAttribute('aria-pressed', active);
    });

    const visiblePins = this.pins.filter((pin) => this.isVisible(pin));
    const visibleHandles = new Set(visiblePins.map((pin) => pin.dataset.stockistPin));

    // Pins from other states stay on the map but are dimmed and inert. Duplicate pins (stockists
    // in several states) are only dimmed once, and not at all if the stockist is in this state.
    this.pins.forEach((pin) => {
      const visible = visiblePins.includes(pin);
      const dimmed = !visible && pin.hasAttribute('data-primary') && !visibleHandles.has(pin.dataset.stockistPin);
      pin.hidden = !visible && !dimmed;
      pin.toggleAttribute('data-dimmed', dimmed);
      pin.toggleAttribute('inert', dimmed);
    });

    this.items.forEach((item) => {
      item.hidden = !this.isVisible(item);
    });

    this.empty.hidden = this.items.some((item) => !item.hidden);
    this.selected = null;
    this.previewed = null;
    this.render();
  }

  select(handle) {
    this.selected = handle;
    this.previewed = null;
    this.render();
  }

  preview(handle) {
    this.previewed = handle;
    this.render();
  }

  close() {
    const handle = this.current;
    this.selected = null;
    this.previewed = null;
    this.render();
    this.bars.find((bar) => bar.dataset.stockistBar === handle && !bar.closest('[data-stockist-item]').hidden)?.focus();
  }

  render() {
    const handle = this.current;

    this.pins.forEach((pin) => {
      pin.dataset.state = !pin.hidden && !pin.hasAttribute('data-dimmed') && pin.dataset.stockistPin === handle ? 'active' : 'inactive';
    });

    this.bars.forEach((bar) => {
      const active = bar.dataset.stockistBar === handle;
      bar.dataset.state = active ? 'active' : 'inactive';
      bar.setAttribute('aria-expanded', active);
    });

    this.cards.forEach((card) => {
      card.hidden = card.dataset.stockistCard !== handle;
    });

    this.panel.dataset.card = handle ? 'open' : 'closed';
    this.drawLine();
  }

  drawLine() {
    const handle = this.current;
    const pin = handle && this.pins.find((item) => item.dataset.stockistPin === handle && !item.hidden && !item.hasAttribute('data-dimmed'));
    const header = handle && this.querySelector(`[data-stockist-card="${handle}"] [data-stockist-card-header]`);

    if (!this.line) return;
    if (!pin || !header) {
      this.line.setAttribute('visibility', 'hidden');
      return;
    }

    const gridRect = this.grid.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const pinRect = pin.getBoundingClientRect();

    // From the bottom-left corner of the card header to the centre of the pin head
    this.line.setAttribute('x1', headerRect.left - gridRect.left);
    this.line.setAttribute('y1', headerRect.bottom - gridRect.top);
    this.line.setAttribute('x2', pinRect.left + pinRect.width / 2 - gridRect.left);
    this.line.setAttribute('y2', pinRect.top + pinRect.height * 0.4 - gridRect.top);
    this.line.setAttribute('visibility', 'visible');
  }
}

customElements.define('stockist-finder', StockistFinder);
